import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, Inject, PLATFORM_ID, Output, EventEmitter, NgZone, Input, HostListener } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { PreviewV2Service } from '../../../services/v2/preview-v2.service';
import { FormBuilderV2Service } from '../../../services/v2/form-builder-v2.service';
import { PreviewData } from '../../../interfaces/api.interface';
import { WebsiteFileService } from '../../../services/website-file.service';
import { ChatPhase } from '../../../interfaces/ai-content.interface';
import { IndexedDBService } from '../../../services/indexeddb.service';
import { ContentEditRequest } from '../../preview/inline-content-editor/inline-content-editor.component';
import { ImageUploadRequest } from '../../preview/inline-image-uploader/inline-image-uploader.component';
import { COLOR_THEMES } from '../../../models/color-theme.model';

@Component({
  selector: 'app-form-builder-preview',
  imports: [CommonModule],
  templateUrl: './form-builder-preview.component.html',
  styleUrls: ['./form-builder-preview.component.scss']
})
export class FormBuilderPreviewComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('previewFrame') previewFrame!: ElementRef<HTMLIFrameElement>;
  
  private destroy$ = new Subject<void>();
  
  selectedTemplate: number = 1;
  selectedTheme: string = 'default';
  isLoading: boolean = false;
  hasTemplateSelected: boolean = false;
  
  // Deployment-related properties
  currentPhase: ChatPhase = ChatPhase.DATA_COLLECTION;
  isDeploying: boolean = false;
  isLogoUploadCompleted: boolean = false;
  showColorPalette: boolean = false;
  
  // Save progress state
  isSavingProgress: boolean = false;
  
  // Edit mode state
  isEditMode: boolean = false;
  hasAIContent: boolean = false;
  
  // Inline content editing
  @Output() openInlineEditor = new EventEmitter<ContentEditRequest>();
  
  // Inline image uploading
  @Output() imageUploaderRequested = new EventEmitter<ImageUploadRequest>();
  
  // Preview loading state
  @Output() previewLoadingStateChanged = new EventEmitter<boolean>();
  
  // Theme picker properties
  colorThemes = COLOR_THEMES;
  showThemePicker: boolean = false;
  
  // Input properties for V2 integration
  @Input() supplierData: any = {};
  @Input() templateId: number = 1;
  @Input() themeKey: string = 'default';
  
  // Dynamic scaling properties
  private readonly TEMPLATE_WIDTH = 1440;  // Template's native width
  private readonly TEMPLATE_HEIGHT = 1130; // Template's minimum height
  private currentScale: number = 0.57; // Default scale
  private resizeTimeout: any;
  private resizeObserver: ResizeObserver | null = null;
  private isScalingInitialized: boolean = false;
  
  // ADD: Iframe loading timeout to prevent stuck loading states
  private iframeLoadingTimeout: any;
  
  ChatPhase = ChatPhase;

  constructor(
    private previewService: PreviewV2Service,
    private formBuilderV2Service: FormBuilderV2Service,
    private websiteFileService: WebsiteFileService,
    private indexedDBService: IndexedDBService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private ngZone: NgZone
  ) { }

  // Listen for window resize events with debouncing
  @HostListener('window:resize', [])
  onWindowResize(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    // Debounce resize events to improve performance
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    this.resizeTimeout = setTimeout(() => {
      console.log('🪟 Window resized, recalculating scale for resolution:', window.innerWidth);
      this.calculateAndApplyScale();
    }, 100);
  }

  ngOnInit(): void {
    console.log('FormBuilderPreviewComponent: Component initializing...');
    
    // Subscribe to template changes from V2 service
    this.formBuilderV2Service.selectedTemplate$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async template => {
        console.log('🎯 DEBUG: Template changed to:', template);
        if (template && template > 0) {
          this.hasTemplateSelected = true;
          this.selectedTemplate = template;
          
          // Show loader during template initialization
          console.log('🎯 DEBUG: Setting isLoading = true for template:', template);
          this.isLoading = true;
          this.previewLoadingStateChanged.emit(true);
          console.log('🎯 DEBUG: Emitted previewLoadingStateChanged(true)');
          
          // ADD: Set timeout fallback (10 seconds) to prevent stuck loading states
          if (this.iframeLoadingTimeout) {
            clearTimeout(this.iframeLoadingTimeout);
            console.log('🎯 DEBUG: Cleared existing timeout');
          }
          this.iframeLoadingTimeout = setTimeout(() => {
            if (this.isLoading) {
              console.warn('🎯 DEBUG: Iframe loading timeout triggered - clearing loading state');
              this.isLoading = false;
              this.previewLoadingStateChanged.emit(false);
            }
          }, 20000);
          console.log('🎯 DEBUG: Set 10-second timeout fallback');
          
          try {
            // CRITICAL FIX: Don't initialize preview here - let FormBuilderV2Component handle it
            // The FormBuilderV2Component already calls initializePreviewWithRestoration()
            // which handles both initialization and content restoration
            console.log('🎯 DEBUG: Template change detected, waiting for FormBuilderV2Component to handle initialization');
            
            // No artificial delay - let the iframe load naturally
            // The loading state will be cleared when iframe loads
            
          } catch (error) {
            console.error('🎯 DEBUG: Error initializing template:', error);
            // Loading state will be cleared when iframe loads
          }
        } else {
          this.hasTemplateSelected = false;
          this.isLoading = false;
          this.previewLoadingStateChanged.emit(false);
          console.log('🎯 DEBUG: No template selected - cleared loading state');
        }
      });

    // CRITICAL FIX: Safeguard for initial template loading
    // Ensure template is initialized by checking the form data after a delay
    setTimeout(() => {
      if (!this.hasTemplateSelected && this.selectedTemplate <= 0) {
        console.log('FormBuilderPreviewComponent: Template not initialized, setting default template');
        this.hasTemplateSelected = true;
        this.selectedTemplate = 1; // Default template
        this.isLoading = false;
      }
    }, 300);

    // Subscribe to theme changes from V2 service
    this.formBuilderV2Service.selectedColorTheme$
      .pipe(takeUntil(this.destroy$))
      .subscribe(theme => {
        console.log('FormBuilderPreviewComponent: Theme changed to:', theme);
        this.selectedTheme = theme;
      });

    // Subscribe to preview updates
    this.previewService.previewUpdate$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        console.log('FormBuilderPreviewComponent: Received preview update', {
          hasData: !!data,
          template: data?.template,
          theme: data?.theme,
          companyName: data?.supplierData?.companyName,
          htmlLength: data?.templateHtml?.length
        });
        if (data && this.hasTemplateSelected) {
          console.log('FormBuilderPreviewComponent: Updating preview with data');
          this.updatePreview(data);
          
          // Force iframe reload after a short delay to ensure changes are applied
          setTimeout(() => {
            if (this.previewFrame && this.previewFrame.nativeElement) {
              const currentSrc = this.previewFrame.nativeElement.srcdoc;
              if (currentSrc) {
                // Force reload by temporarily clearing and resetting srcdoc
                this.previewFrame.nativeElement.srcdoc = '';
                setTimeout(() => {
                  this.previewFrame.nativeElement.srcdoc = currentSrc;
                }, 50);
              }
            }
          }, 100);
        } else {
          console.log('FormBuilderPreviewComponent: No preview update - no data or template not selected');
        }
        // Update hasAIContent when preview updates (in case AI content is applied)
        // this.hasAIContent = this.previewService.hasAIContentBeenGenerated();
      });

    // Subscribe to edit mode changes
    this.previewService.isEditMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(editMode => {
        this.isEditMode = editMode;
        console.log('FormBuilderPreviewComponent: Edit mode changed to:', editMode);
      });

    // Check if AI content has been generated
    // this.hasAIContent = this.previewService.hasAIContentBeenGenerated();
    
    console.log('FormBuilderPreviewComponent: Component initialized');
  }

  ngAfterViewInit(): void {
    // Initialize dynamic scaling
    this.initializeScaling();
    
    // Set up global function for iframe navigation (only in browser)
    if (isPlatformBrowser(this.platformId)) {
      (window as any).loadTemplatePage = (pageName: string) => {
        console.log('FormBuilderPreviewComponent: loadTemplatePage called for:', pageName);
        // Load the template page using the preview service
        this.ngZone.run(async () => {
          try {
            const supplierData = this.getCurrentSupplierData();
            await this.previewService.loadTemplatePage(pageName, supplierData);
            console.log('FormBuilderPreviewComponent: Template page loaded successfully:', pageName);
          } catch (error) {
            console.error('FormBuilderPreviewComponent: Error loading template page:', error);
          }
        });
      };
      
      // Set up message listener for iframe communication
      window.addEventListener('message', (event) => {
        console.log('FormBuilderPreview: Received message from iframe:', event.data);
        if (event.data && event.data.type === 'openContentEditor') {
          const { tagName, currentContent, elementRect } = event.data;
          console.log('FormBuilderPreview: Processing openContentEditor message');
          this.ngZone.run(() => {
            this.openContentEditor(tagName, currentContent, elementRect);
          });
        } else if (event.data && event.data.type === 'openImageUploader') {
          const { tagName, currentImageSrc, elementRect } = event.data;
          console.log('FormBuilderPreview: Processing openImageUploader message');
          console.log('FormBuilderPreview: Image uploader emitter exists:', !!this.imageUploaderRequested);
          this.ngZone.run(() => {
            this.openImageUploaderHandler(tagName, currentImageSrc, elementRect);
          });
        }
      });
      
      // Set up click outside handler for theme picker
      document.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const themePickerContainer = target.closest('.theme-picker-container');
        if (!themePickerContainer && this.showThemePicker) {
          this.ngZone.run(() => {
            this.showThemePicker = false;
          });
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Clean up resize timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    // ADD: Clear iframe loading timeout
    if (this.iframeLoadingTimeout) {
      clearTimeout(this.iframeLoadingTimeout);
      this.iframeLoadingTimeout = null;
    }
    
    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    
    // ADD: Clear loading states on destroy
    this.isLoading = false;
    this.previewLoadingStateChanged.emit(false);
  }

  /**
   * Calculate optimal scale and apply dynamic dimensions (width & height) to iframe
   */
  private calculateAndApplyScale(forceUpdate: boolean = false): void {
    if (!this.previewFrame || !isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const iframe = this.previewFrame.nativeElement;
      const container = iframe.parentElement;
      
      if (!container) {
        console.warn('No container found for iframe scaling');
        return;
      }

      // Wait for container to have proper dimensions
      const containerRect = container.getBoundingClientRect();
      const availableWidth = containerRect.width;
      const availableHeight = containerRect.height;
      
      // Skip calculation if container dimensions are not ready
      if (availableWidth <= 0 || availableHeight <= 0) {
        console.log('Container dimensions not ready, retrying...');
        setTimeout(() => this.calculateAndApplyScale(forceUpdate), 50);
        return;
      }
      
      // Get window dimensions for additional context
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      
      // Calculate scale based on width (primary constraint for 1440px template)
      let scale = availableWidth / this.TEMPLATE_WIDTH;
      
      // Check if the scaled height would fit in the available height
      const scaledHeight = this.TEMPLATE_HEIGHT * scale;
      if (scaledHeight > availableHeight) {
        // If height doesn't fit, scale based on height instead
        scale = availableHeight / this.TEMPLATE_HEIGHT;
      }
      
      // Apply responsive adjustments based on screen size categories
      scale = this.getResponsiveScale(scale, windowWidth, windowHeight, availableWidth, availableHeight);
      
      // Ensure scale bounds for usability
      const minScale = 0.2;  // Minimum readable scale
      const maxScale = 1.5;  // Maximum reasonable scale
      const finalScale = Math.max(minScale, Math.min(maxScale, scale));
      
      // Calculate compensated dimensions to account for transform scale
      const compensatedHeight = Math.round(windowHeight / finalScale);
      const compensatedWidth = Math.round(availableWidth / finalScale);
      
      console.log('🎯 Iframe scaling calculation:', {
        windowSize: `${windowWidth}x${windowHeight}`,
        availableSize: `${availableWidth.toFixed(0)}x${availableHeight.toFixed(0)}`,
        baseScale: (availableWidth / this.TEMPLATE_WIDTH).toFixed(4),
        finalScale: finalScale.toFixed(4),
        compensatedSize: `${compensatedWidth}x${compensatedHeight}`,
        isInitialLoad: !this.isScalingInitialized,
        forceUpdate: forceUpdate
      });
      
      // Apply scaling with proper timing and force reflow
      this.applyScalingWithReflow(iframe, finalScale, compensatedWidth, compensatedHeight, container, forceUpdate);
      
      // Mark scaling as initialized
      if (!this.isScalingInitialized) {
        this.isScalingInitialized = true;
        console.log('✅ Initial scaling applied for resolution:', windowWidth);
      }
      
    } catch (error) {
      console.error('Error calculating iframe scale:', error);
    }
  }

  /**
   * Apply scaling with proper timing and force reflow to ensure consistent rendering
   */
  private applyScalingWithReflow(iframe: HTMLIFrameElement, scale: number, width: number, height: number, container: HTMLElement, forceUpdate: boolean = false): void {
    // Store current scale for comparison
    const previousScale = this.currentScale;
    const scaleDifference = Math.abs(scale - previousScale);
    
    // For initial load or forced updates, always apply the scale regardless of threshold
    const shouldUpdateScale = forceUpdate || !this.isScalingInitialized || scaleDifference > 0.005;
    
    console.log('🔧 Scale application check:', {
      newScale: scale.toFixed(4),
      previousScale: previousScale.toFixed(4),
      scaleDifference: scaleDifference.toFixed(4),
      forceUpdate: forceUpdate,
      isInitialLoad: !this.isScalingInitialized,
      shouldUpdateScale: shouldUpdateScale,
      windowWidth: window.innerWidth
    });
    
    // Step 1: Set iframe dimensions first (without transform)
    iframe.style.setProperty('width', `${width}px`, 'important');
    iframe.style.setProperty('height', `${height}px`, 'important');
    iframe.style.setProperty('min-width', `${width}px`, 'important');
    iframe.style.setProperty('min-height', `${height}px`, 'important');
    iframe.style.setProperty('max-width', 'none', 'important');
    iframe.style.setProperty('max-height', 'none', 'important');
    iframe.style.boxSizing = 'border-box';
    iframe.style.overflow = 'hidden';
    iframe.style.display = 'block';
    
    // Step 2: Ensure container supports full dimensions
    this.updateContainerDimensions(container, width, height);
    
    // Step 3: Force a reflow before applying transform
    iframe.offsetHeight; // Force reflow
    
    // Step 4: Apply transform with proper timing
    if (shouldUpdateScale) {
      this.currentScale = scale;
      
      // Use requestAnimationFrame to ensure proper timing
      requestAnimationFrame(() => {
        // Set transform origin first
        iframe.style.transformOrigin = 'top left';
        
        // Force another reflow
        iframe.offsetHeight;
        
        // Apply the transform
        iframe.style.transform = `scale(${scale})`;
        
        // Force final reflow to ensure transform is applied
        iframe.offsetHeight;
        
        console.log('✅ Transform applied successfully:', {
          scale: scale.toFixed(4),
          transformOrigin: 'top left',
          transform: `scale(${scale})`,
          windowWidth: window.innerWidth,
          reason: forceUpdate ? 'forced' : !this.isScalingInitialized ? 'initial' : 'threshold'
        });
      });
    } else {
      console.log('⚠️ Transform NOT applied:', {
        scaleDifference: scaleDifference.toFixed(4),
        threshold: '0.005',
        forceUpdate: forceUpdate,
        windowWidth: window.innerWidth
      });
    }
  }

  /**
   * Update container dimensions to support the scaled iframe
   */
  private updateContainerDimensions(container: HTMLElement, width: number, height: number): void {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const availableWidth = container.getBoundingClientRect().width;
    
    // Update the preview container
    const previewContainer = container.closest('.preview-container') as HTMLElement;
    if (previewContainer) {
      previewContainer.style.setProperty('width', `${windowWidth}px`, 'important');
      previewContainer.style.setProperty('height', `${windowHeight}px`, 'important');
      previewContainer.style.setProperty('min-width', `${windowWidth}px`, 'important');
      previewContainer.style.setProperty('min-height', `${windowHeight}px`, 'important');
      previewContainer.style.setProperty('max-width', 'none', 'important');
      previewContainer.style.setProperty('max-height', 'none', 'important');
      previewContainer.style.setProperty('overflow', 'hidden', 'important');
      previewContainer.style.setProperty('box-sizing', 'border-box', 'important');
    }
    
    // Update the preview content container
    if (container) {
      container.style.setProperty('width', `${availableWidth}px`, 'important');
      container.style.setProperty('height', `${windowHeight}px`, 'important');
      container.style.setProperty('min-width', `${availableWidth}px`, 'important');
      container.style.setProperty('min-height', `${windowHeight}px`, 'important');
      container.style.setProperty('max-width', 'none', 'important');
      container.style.setProperty('max-height', 'none', 'important');
      container.style.setProperty('padding', '0', 'important');
      container.style.setProperty('margin', '0', 'important');
      container.style.setProperty('box-sizing', 'border-box', 'important');
      container.style.setProperty('overflow', 'hidden', 'important');
    }
  }

  /**
   * Get responsive scale adjustments based on screen size and device category
   */
  private getResponsiveScale(baseScale: number, windowWidth: number, windowHeight: number, availableWidth: number, availableHeight: number): number {
    // Get device category for targeted adjustments
    const deviceCategory = this.getDeviceCategory(windowWidth);
    
    let adjustedScale = baseScale;
    
    // Apply device-specific fine-tuning
    switch (deviceCategory) {
      case 'mobile':
        // Mobile devices: ensure content is visible but not too small
        adjustedScale = Math.max(baseScale * 0.95, 0.25);
        break;
        
      case 'tablet':
        // Tablets: balance between visibility and content size
        adjustedScale = baseScale * 0.98;
        break;
        
      case 'laptop':
        // Laptops: standard scaling with slight adjustment for common sizes
        if (windowWidth <= 1366) {
          adjustedScale = baseScale * 0.96; // Slight reduction for smaller laptops
        } else {
          adjustedScale = baseScale; // Keep calculated scale for larger laptops
        }
        break;
        
      case 'desktop':
        // Desktops: use calculated scale with precise adjustments for different ranges
        if (windowWidth >= 1800) {
          // Precise scale calculation for screens 1800px and above
          const scaleMultiplier = this.calculateScaleMultiplierForLargeScreens(windowWidth);
          adjustedScale = baseScale * scaleMultiplier;
          
          // Debug logging for large screen scaling
          console.log('Large screen scaling (1800px+):', {
            windowWidth,
            baseScale: baseScale.toFixed(4),
            scaleMultiplier: scaleMultiplier.toFixed(4),
            finalScale: adjustedScale.toFixed(4)
          });
        } else {
          adjustedScale = baseScale;
        }
        break;
        
      case 'ultrawide':
        // Ultra-wide displays: increase scale but maintain readability
        adjustedScale = Math.min(baseScale * 1.15, 1.4);
        break;
    }
    
    // Additional adjustments for aspect ratio extremes
    const aspectRatio = windowWidth / windowHeight;
    
    // Very wide screens (ultra-wide aspect ratios)
    if (aspectRatio > 2.2) {
      adjustedScale = adjustedScale * 1.08;
    }
    // Very tall screens (portrait or very tall windows)
    else if (aspectRatio < 0.75) {
      adjustedScale = adjustedScale * 0.92;
    }
    
    // Ensure minimum visibility on very small containers
    if (availableWidth < 400) {
      adjustedScale = Math.max(adjustedScale, 0.3);
    }
    
    return adjustedScale;
  }

  /**
   * Calculate precise scale multiplier for large screens (1800px+)
   */
  private calculateScaleMultiplierForLargeScreens(windowWidth: number): number {
    // Precise scale calculation based on window width
    if (windowWidth >= 1800 && windowWidth <= 1850) {
      // 1800-1850px: Scale multiplier 0.90-0.92
      const progress = (windowWidth - 1800) / 50;
      return 0.90 + (progress * 0.02);
    } else if (windowWidth >= 1851 && windowWidth <= 1900) {
      // 1851-1900px: Scale multiplier 0.92-0.94
      const progress = (windowWidth - 1851) / 49;
      return 0.92 + (progress * 0.02);
    } else if (windowWidth >= 1901 && windowWidth <= 1920) {
      // 1901-1920px: Scale multiplier 0.94-0.95
      const progress = (windowWidth - 1901) / 19;
      return 0.94 + (progress * 0.01);
    } else if (windowWidth >= 1921 && windowWidth <= 2000) {
      // 1921-2000px: Scale multiplier 0.95-0.98
      const progress = (windowWidth - 1921) / 79;
      return 0.95 + (progress * 0.03);
    } else if (windowWidth >= 2001 && windowWidth <= 2200) {
      // 2001-2200px: Scale multiplier 0.98-1.00
      const progress = (windowWidth - 2001) / 199;
      return 0.98 + (progress * 0.02);
    } else if (windowWidth >= 2201 && windowWidth <= 2560) {
      // 2201-2560px: Scale multiplier 1.00-1.05
      const progress = (windowWidth - 2201) / 359;
      return 1.00 + (progress * 0.05);
    } else if (windowWidth > 2560) {
      // Ultra-wide displays: Cap at 1.08
      return Math.min(1.08, 1.05 + ((windowWidth - 2560) / 1000) * 0.03);
    }
    
    // Fallback for edge cases
    return 0.90;
  }

  /**
   * Categorize device type based on window width
   */
  private getDeviceCategory(windowWidth: number): string {
    if (windowWidth <= 768) {
      return 'mobile';
    } else if (windowWidth <= 1024) {
      return 'tablet';
    } else if (windowWidth <= 1600) {
      return 'laptop';
    } else if (windowWidth <= 2560) {
      return 'desktop';
    } else {
      return 'ultrawide';
    }
  }

  /**
   * Initialize responsive scaling system with ResizeObserver for reliable dimension tracking
   */
  private initializeScaling(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    
    console.log('🚀 Initializing responsive scaling system...');
    
    // Force initial scaling as soon as possible
    setTimeout(() => {
      this.calculateAndApplyScale(true); // Force initial application
    }, 10);
    
    // Setup ResizeObserver for container dimension changes
    this.setupResizeObserver();
    
    // Backup initialization for edge cases
    setTimeout(() => {
      if (!this.isScalingInitialized) {
        console.log('🔄 Backup scaling initialization...');
        this.calculateAndApplyScale(true);
      }
    }, 200);
  }

  /**
   * Setup ResizeObserver to monitor container dimension changes
   */
  private setupResizeObserver(): void {
    if (!isPlatformBrowser(this.platformId) || !window.ResizeObserver) {
      console.log('ResizeObserver not available, falling back to window resize events');
      return;
    }

    // Create ResizeObserver to watch for container size changes
    this.resizeObserver = new ResizeObserver((entries) => {
      // Debounce resize events to improve performance
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      
      this.resizeTimeout = setTimeout(() => {
        console.log('📐 Container dimensions changed, recalculating scale...');
        this.calculateAndApplyScale();
      }, 50);
    });

    // Start observing the preview container and iframe
    setTimeout(() => {
      if (this.previewFrame && this.previewFrame.nativeElement.parentElement) {
        this.resizeObserver?.observe(this.previewFrame.nativeElement.parentElement);
        console.log('✅ ResizeObserver monitoring preview container');
      }
    }, 100);
  }



  private updatePreview(data: PreviewData): void {
    if (!this.previewFrame) {
      console.log('🎯 DEBUG: No preview frame available');
      return;
    }

    // REMOVE: Redundant loading state - template subscription already handles this with timeout
    // this.isLoading = true;

    const html = this.previewService.generatePreviewHtml(data);    
    // CRITICAL FIX: Apply scaling before setting new content to ensure proper rendering
    this.calculateAndApplyScale();
    
    // Force iframe refresh by clearing srcdoc first, then setting it
    console.log('🎯 DEBUG: Clearing iframe srcdoc');
    this.previewFrame.nativeElement.srcdoc = '';
    setTimeout(() => {
      // Add cache-busting parameter to force reload, especially for images
      const cacheBustedHtml = html.replace(/src="([^"]*\.(png|jpg|jpeg|gif|webp|svg))"/gi, (match, src) => {
        const separator = src.includes('?') ? '&' : '?';
        return `src="${src}${separator}t=${Date.now()}"`;
      });
      console.log('🎯 DEBUG: Setting new iframe srcdoc content');
      this.previewFrame.nativeElement.srcdoc = cacheBustedHtml;
      console.log('🎯 DEBUG: iframe srcdoc set - waiting for load event');
      
      // Apply scaling again after content is set
      setTimeout(() => {
        this.calculateAndApplyScale();
      }, 50);
    }, 10);
  }

  onIframeLoad(): void {
    
    // ADD: Clear timeout when iframe loads successfully
    if (this.iframeLoadingTimeout) {
      clearTimeout(this.iframeLoadingTimeout);
      this.iframeLoadingTimeout = null;
    }
    
    // Clear loading state from template changes (updatePreview no longer sets loading state)
    this.isLoading = false;
    this.previewLoadingStateChanged.emit(false);
    
    // Force scaling application after iframe content is fully loaded
    setTimeout(() => {
      this.calculateAndApplyScale(true); // Force update after content load
    }, 50);
    
    // Additional scaling attempt after a longer delay for complex content
    setTimeout(() => {
      this.calculateAndApplyScale();
    }, 200);
  }


  /**
   * Check if all questions are completed to enable deployment
   */
  allQuestionsCompleted(): boolean {
    // V2 doesn't have the same question flow, so we'll return true for now
    return true;
  }

  /**
   * Check if deploy button should be visible (always visible)
   */
  shouldShowDeployButton(): boolean {
    return true;
  }

  /**
   * Check if deploy button should be enabled
   */
  isDeployButtonEnabled(): boolean {
    return this.currentPhase === ChatPhase.DEPLOYMENT && !this.isDeploying;
  }

  /**
   * Deploy the website by calling the V2 service
   */
  async deployWebsite(): Promise<void> {
    // Switch to browse mode before deployment to show clean preview
    if (this.isEditMode) {
      await this.previewService.setEditMode(false, this.formBuilderV2Service.getCompanyGuid());
      console.log('🔄 Switched to browse mode for deployment');
    }
    
    // Blur functionality disabled - no blur removal for deployment
    
    // TODO: Implement V2 deployment
    console.log('🚀 V2 deployment requested');
  }

  /**
   * Toggle edit mode on/off
   */
  async toggleEditMode(): Promise<void> {
    await this.previewService.toggleEditMode(this.formBuilderV2Service.getCompanyGuid());
    // Refresh preview to apply/remove edit functionality
    await this.refreshPreview();
  }

  /**
   * Refresh the current preview
   */
  async refreshPreview(): Promise<void> {
    // Refresh the current preview
    const currentData = this.getCurrentSupplierData();
    this.previewService.updatePreview(currentData, this.selectedTemplate, this.selectedTheme, this.formBuilderV2Service.getCompanyGuid(), this.isEditMode ? 'true' : null);
    
    // Debug info
    console.log(`Preview refreshed: ${this.websiteFileService.getFileCount()} files loaded`);
  }

  /**
   * Open the inline content editor
   */
  openContentEditor(tagName: string, currentContent: string, elementRect: DOMRect): void {
    console.log('FormBuilderPreview: Opening content editor for tag:', tagName, 'Content:', currentContent);
    
    const editRequest = {
      tagName,
      currentContent,
      elementPosition: {
        x: elementRect.left,
        y: elementRect.top,
        width: elementRect.width,
        height: elementRect.height
      }
    };
    
    // Emit event for parent component to handle
    this.openInlineEditor.emit(editRequest);
    console.log('🔥 FormBuilderPreview: Content Editor event emitted for tag:', tagName);
  }

  /**
   * Open the inline image uploader
   */
  openImageUploaderHandler(tagName: string, currentImageSrc: string, elementRect: DOMRect): void {
    console.log('FormBuilderPreview: Opening image uploader for tag:', tagName, 'Image:', currentImageSrc);
    console.log('FormBuilderPreview: Element rect:', elementRect);
    
    const uploadRequest = {
      tagName,
      currentImageSrc,
      elementPosition: {
        x: elementRect.left,
        y: elementRect.top,
        width: elementRect.width,
        height: elementRect.height
      }
    };
    
    console.log('FormBuilderPreview: About to emit openImageUploader event with:', uploadRequest);
    
    // Emit event for parent component to handle
    this.imageUploaderRequested.emit(uploadRequest);
    console.log('🔥 FormBuilderPreview: Image Uploader event emitted for tag:', tagName);
  }

  /**
   * Get current supplier data for the editor
   */
  getCurrentSupplierData(): any {
    return this.supplierData;
  }

  /**
   * Get current semantic content for the editor
   */
  getCurrentSemanticContent(): { [tagName: string]: string } {
    return this.previewService.getCurrentSemanticContent();
  }

  /**
   * Handle content update from the editor
   */
  async onContentUpdated(update: { tagName: string; newContent: string }): Promise<void> {
    // Update the content in the preview
    await this.previewService.updateContentInPreview(update.tagName, update.newContent, this.formBuilderV2Service.getCompanyGuid());
  }

  /**
   * Handle editor cancellation
   */
  onEditCancelled(): void {
    // Handle edit cancellation
    console.log('FormBuilderPreview: Edit cancelled');
  }

  /**
   * Handle keep old content action
   */
  onKeepOldContent(): void {
    // Handle keep old content
    console.log('FormBuilderPreview: Keep old content');
  }

  /**
   * Save user progress to IndexedDB
   */
  async saveProgress(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('Save progress is only available in browser environment');
      return;
    }

    this.isSavingProgress = true;

    try {
      console.log('💾 Starting to save user progress...');

      // Save supplier data
      await this.indexedDBService.setUserSupplierData(this.formBuilderV2Service.getCompanyGuid(), this.supplierData);
      console.log('✅ Supplier data saved');

      // Save selected theme
      await this.indexedDBService.setUserTheme(this.formBuilderV2Service.getCompanyGuid(), this.selectedTheme);
      console.log('✅ Theme preference saved');

      // Save selected template
      await this.indexedDBService.storeMetadata('selectedTemplate', this.selectedTemplate);
      console.log('✅ Template selection saved');

      console.log('🎉 All progress saved successfully');

    } catch (error) {
      console.error('❌ Failed to save progress:', error);
    } finally {
      this.isSavingProgress = false;
    }
  }

  /**
   * Toggle theme picker dropdown
   */
  toggleThemePicker(): void {
    this.showThemePicker = !this.showThemePicker;
  }

  /**
   * Select a theme and apply it
   */
  async selectTheme(themeKey: string): Promise<void> {
    // Update the selected theme in V2 service
    await this.formBuilderV2Service.updateTheme(themeKey);
    
    // Save theme preference to IndexedDB
    if (isPlatformBrowser(this.platformId)) {
      await this.indexedDBService.setUserTheme(this.formBuilderV2Service.getCompanyGuid(), themeKey);
    }
    
    // Hide the theme picker
    this.showThemePicker = false;
    
    console.log('✅ Theme changed to:', themeKey);
  }
} 